import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { AuthGuard } from '../auth/AuthGuard';
import { AppShell } from './components/AppShell';

function GuestOnlyRoute({ element }: { element: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{element}</>;
}

// ─── Auth views (public) ──────────────────────────────────────────────────────

const AuthCallbackView = lazy(() => import('./views/AuthCallbackView'));
const AuthErrorView = lazy(() => import('./views/AuthErrorView'));
const LoginView = lazy(() => import('./views/LoginView'));

// ─── Core views ───────────────────────────────────────────────────────────────

const DashboardView = lazy(() => import('./views/DashboardView'));
const SettingsView = lazy(() => import('./views/SettingsView'));
const PreferencesView = lazy(() => import('./views/PreferencesView'));
const I18nDemoView = lazy(() => import('./views/I18nDemoView'));

// ─── Restaurant views ─────────────────────────────────────────────────────────

const RestaurantsView = lazy(() => import('./views/RestaurantsView'));
const RestaurantCreateView = lazy(() => import('./views/RestaurantCreateView'));
const RestaurantDetailView = lazy(() => import('./views/RestaurantDetailView'));
const RestaurantActiveMenusView = lazy(() => import('./views/RestaurantActiveMenusView'));
const RestaurantLinkMenusView = lazy(() => import('./views/RestaurantLinkMenusView'));
const RestaurantAIInstructionsView = lazy(() => import('./views/RestaurantAIInstructionsView'));
const RestaurantTablesView = lazy(() => import('./views/RestaurantTablesView'));
const RestaurantEditView = lazy(() => import('./views/RestaurantEditView'));

// ─── Plate views ──────────────────────────────────────────────────────────────

const PlatesView = lazy(() => import('./views/PlatesView'));
const PlateCreateView = lazy(() => import('./views/PlateCreateView'));
const PlateEditView = lazy(() => import('./views/PlateEditView'));

// ─── Menu views ───────────────────────────────────────────────────────────────

const MenusView = lazy(() => import('./views/MenusView'));
const MenuCreateView = lazy(() => import('./views/MenuCreateView'));
const MenuDetailView = lazy(() => import('./views/MenuDetailView'));
const MenuEditView = lazy(() => import('./views/MenuEditView'));
const MenuCategoryWizardView = lazy(() => import('./views/MenuCategoryWizardView'));
const MenuCategoryItemsView = lazy(() => import('./views/MenuCategoryItemsView'));

// ─── Beverage views ───────────────────────────────────────────────────────────

const BeveragesView = lazy(() => import('./views/BeveragesView'));
const BeverageCreateView = lazy(() => import('./views/BeverageCreateView'));
const BeverageEditView = lazy(() => import('./views/BeverageEditView'));

// ─── Customisation views ──────────────────────────────────────────────────────

const CustomisationsView = lazy(() => import('./views/CustomisationsView'));
const CustomisationEditView = lazy(() => import('./views/CustomisationEditView'));
const AttachCustomizationWizardView = lazy(() => import('./views/AttachCustomizationWizardView'));

// ─── Waiter views ─────────────────────────────────────────────────────────────

const WaitersView = lazy(() => import('./views/WaitersView'));
const WaiterProfileView = lazy(() => import('./views/WaiterProfileView'));
const WaiterFormView = lazy(() => import('./views/WaiterFormView'));

// ─── Other feature views ──────────────────────────────────────────────────────

const OrdersView = lazy(() => import('./views/OrdersView'));
const StaffView = lazy(() => import('./views/StaffView'));
const TablesView = lazy(() => import('./views/TablesView'));
const AiChatView = lazy(() => import('./views/AiChatView'));
const TagsView = lazy(() => import('./views/TagsView'));

// ─── Helper ───────────────────────────────────────────────────────────────────

function Protected({ children }: { children: React.ReactNode }) {
  return (
    <AuthGuard>
      <AppShell>{children}</AppShell>
    </AuthGuard>
  );
}

// ─── Router ───────────────────────────────────────────────────────────────────

export function AppRouter() {
  return (
    <Routes>
      {/* Auth routes — public, no shell, no guard */}
      <Route path="/login" element={<GuestOnlyRoute element={<LoginView />} />} />
      <Route path="/auth/callback" element={<AuthCallbackView />} />
      <Route path="/auth/error" element={<AuthErrorView />} />

      {/* Root redirect */}
      <Route
        path="/"
        element={
          <Protected>
            <Navigate to="/dashboard" replace />
          </Protected>
        }
      />

      {/* Core */}
      <Route
        path="/dashboard"
        element={
          <Protected>
            <DashboardView />
          </Protected>
        }
      />
      <Route
        path="/i18n"
        element={
          <Protected>
            <I18nDemoView />
          </Protected>
        }
      />
      <Route
        path="/settings"
        element={
          <Protected>
            <SettingsView />
          </Protected>
        }
      />
      <Route
        path="/settings/:section"
        element={
          <Protected>
            <SettingsView />
          </Protected>
        }
      />
      <Route
        path="/settings/:section/:subSection"
        element={
          <Protected>
            <SettingsView />
          </Protected>
        }
      />
      <Route
        path="/preferences"
        element={
          <Protected>
            <PreferencesView />
          </Protected>
        }
      />
      <Route
        path="/preferences/:section"
        element={
          <Protected>
            <PreferencesView />
          </Protected>
        }
      />

      {/* Restaurants */}
      <Route
        path="/restaurants"
        element={
          <Protected>
            <RestaurantsView />
          </Protected>
        }
      />
      <Route
        path="/restaurants/new"
        element={
          <Protected>
            <RestaurantCreateView />
          </Protected>
        }
      />
      <Route
        path="/restaurants/:id/edit"
        element={
          <Protected>
            <RestaurantEditView />
          </Protected>
        }
      />
      <Route
        path="/restaurants/:id"
        element={
          <Protected>
            <RestaurantDetailView />
          </Protected>
        }
      />
      <Route
        path="/restaurants/:id/tables"
        element={
          <Protected>
            <RestaurantTablesView />
          </Protected>
        }
      />
      <Route
        path="/restaurants/:id/menus"
        element={
          <Protected>
            <RestaurantActiveMenusView />
          </Protected>
        }
      />
      <Route
        path="/restaurants/:id/menus/link"
        element={
          <Protected>
            <RestaurantLinkMenusView />
          </Protected>
        }
      />
      <Route
        path="/restaurants/:id/ai-instructions"
        element={
          <Protected>
            <RestaurantAIInstructionsView />
          </Protected>
        }
      />

      {/* Plates */}
      <Route
        path="/plates"
        element={
          <Protected>
            <PlatesView />
          </Protected>
        }
      />
      <Route
        path="/plates/new"
        element={
          <Protected>
            <PlateCreateView />
          </Protected>
        }
      />
      <Route
        path="/plates/:id/edit"
        element={
          <Protected>
            <PlateEditView />
          </Protected>
        }
      />

      {/* Menus */}
      <Route
        path="/menus"
        element={
          <Protected>
            <MenusView />
          </Protected>
        }
      />
      <Route
        path="/menus/new"
        element={
          <Protected>
            <MenuCreateView />
          </Protected>
        }
      />
      <Route
        path="/menus/:id"
        element={
          <Protected>
            <MenuDetailView />
          </Protected>
        }
      />
      <Route
        path="/menus/:id/edit"
        element={
          <Protected>
            <MenuEditView />
          </Protected>
        }
      />
      <Route
        path="/menus/:id/categories/new"
        element={
          <Protected>
            <MenuCategoryWizardView />
          </Protected>
        }
      />
      <Route
        path="/menus/:id/categories/:categoryId/items"
        element={
          <Protected>
            <MenuCategoryItemsView />
          </Protected>
        }
      />

      {/* Beverages */}
      <Route
        path="/beverages"
        element={
          <Protected>
            <BeveragesView />
          </Protected>
        }
      />
      <Route
        path="/beverages/new"
        element={
          <Protected>
            <BeverageCreateView />
          </Protected>
        }
      />
      <Route
        path="/beverages/:id/edit"
        element={
          <Protected>
            <BeverageEditView />
          </Protected>
        }
      />

      {/* Customisations */}
      <Route
        path="/customisations"
        element={
          <Protected>
            <CustomisationsView />
          </Protected>
        }
      />
      <Route
        path="/customisations/:id/edit"
        element={
          <Protected>
            <CustomisationEditView />
          </Protected>
        }
      />
      <Route
        path="/customisations/attach"
        element={
          <Protected>
            <AttachCustomizationWizardView />
          </Protected>
        }
      />

      {/* Waiters */}
      <Route
        path="/waiters"
        element={
          <Protected>
            <WaitersView />
          </Protected>
        }
      />
      <Route
        path="/waiters/:id"
        element={
          <Protected>
            <WaiterProfileView />
          </Protected>
        }
      />
      <Route
        path="/waiters/new"
        element={
          <Protected>
            <WaiterFormView />
          </Protected>
        }
      />
      <Route
        path="/waiters/:id/edit"
        element={
          <Protected>
            <WaiterFormView />
          </Protected>
        }
      />

      {/* Orders */}
      <Route
        path="/orders"
        element={
          <Protected>
            <OrdersView />
          </Protected>
        }
      />

      {/* Staff */}
      <Route
        path="/staff"
        element={
          <Protected>
            <StaffView />
          </Protected>
        }
      />

      {/* Tables */}
      <Route
        path="/tables"
        element={
          <Protected>
            <TablesView />
          </Protected>
        }
      />

      {/* AI */}
      <Route
        path="/ai/chat"
        element={
          <Protected>
            <AiChatView />
          </Protected>
        }
      />

      {/* Settings — tags */}
      <Route
        path="/settings/tags"
        element={
          <Protected>
            <TagsView />
          </Protected>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default AppRouter;
